export class ActivityType {
  static entity_added = "entity_added";
  static entity_deleted = "entity_deleted";
  static relationship_added = "relationship_added";
  static relationship_deleted = "relationship_deleted";
  static relationship_bulk_deleted = "relationship_bulk_deleted";
  static validated_entity = "validated_entity";
  static unvalidated_entity = "unvalidated_entity";
  static relationships_validated = "relationships_validated";
  static relationships_unvalidated = "relationships_unvalidated";
  static summary_changed = "summary_changed";
  static example_text_edited = "example_text_edited";
  // Utility methods
  static values() {
    return [ActivityType.entity_added, ActivityType.relationship_added, ActivityType.validated_entity, ActivityType.unvalidated_entity, ActivityType.relationships_validated, ActivityType.relationships_unvalidated, ActivityType.summary_changed];
  }
}

// To prevent UI crashes with extremely high-cardinality label sets.
// The backend enforces the same limit (MAX_ALLOWED_UNIQUE_LABELS = 200).
export const MAX_LABELS_TO_DISPLAY = 200;

export class ComparisonModeOption {
  static none = "none";
  static teacher = "teacher";
  static all = "all";
  static manual_model_predictions = "manual_model_predictions";

  // Utility methods
  static values() {
    return [ComparisonModeOption.none, ComparisonModeOption.teacher, ComparisonModeOption.all, ComparisonModeOption.manual_model_predictions];
  }
}

export class TaskType {
  static NER = "NERResponse";
  static classification = "ClassificationResponse";
  static NER_WITH_RELATIONS = "EntityRecognitionResponse";
  static summarization = "Summarization";
  static contentGeneration = "ContentGeneration";

  // Utility methods
  static values() {
    return [TaskType.NER, TaskType.classification, TaskType.NER_WITH_RELATIONS, TaskType.summarization, TaskType.contentGeneration];
  }
}

/**
 * Generative task types (decoder models served via vLLM): summarization is a
 * specific case of content generation — both share the same generative stack.
 */
export const isGenerativeTask = (taskType) =>
  taskType === TaskType.summarization || taskType === TaskType.contentGeneration;

/** Case-insensitive variant for call sites that receive task type as a raw string. */
export const isGenerativeTaskLoose = (taskType) =>
  [TaskType.summarization.toLowerCase(), TaskType.contentGeneration.toLowerCase()].includes(
    (taskType || '').toLowerCase(),
  );

/** Case-insensitive classification check for raw task type strings. */
export const isClassificationTaskLoose = (taskType) =>
  (taskType || '').toLowerCase() === TaskType.classification.toLowerCase();

/**
 * Native class-probs providers (System One decision models — TypeSafe AI's
 * Jev) answer typed classification questions with per-label probabilities
 * and confidence; they cannot generate free text, so model pickers only
 * offer them on classification tasks.
 */
export const NATIVE_CLASS_PROBS_PROVIDERS = ['jev'];

/**
 * Whether a provider's models may be offered for the given task type.
 * Only native class-probs providers are restricted (classification only —
 * an unknown/absent task type hides them too, on the safe side).
 */
export const isProviderAllowedForTask = (provider, taskType) => {
  if (!NATIVE_CLASS_PROBS_PROVIDERS.includes(provider)) return true;
  return isClassificationTaskLoose(taskType);
};

/** Noun for the generative output of a task ("summary" vs "response"). */
export const getGenerativeOutputNoun = (taskType) =>
  taskType === TaskType.contentGeneration ? 'response' : 'summary';

export class DatasetOrigin {
  static unknown = "unknown";
  static human_created = "human_created";
  static commercial_model = "commercial_model";
  static other = "other";
  static specific_ai_fully_generated = "specific_ai_fully_generated";
  static specific_ai_labeled = "specific_ai_labeled";
  static specific_ai_agent = "specific_ai_agent";
  static huggingface = "huggingface";
  static sdk = "sdk";

  static values() {
    return [
      DatasetOrigin.unknown,
      DatasetOrigin.human_created,
      DatasetOrigin.commercial_model,
      DatasetOrigin.other,
      DatasetOrigin.specific_ai_fully_generated,
      DatasetOrigin.specific_ai_labeled,
      DatasetOrigin.specific_ai_agent,
      DatasetOrigin.huggingface,
      DatasetOrigin.sdk,
    ];
  }
}



export class SubTaskType {
  static Ner = "NERResponse";
  static Relations = "RelationExtractionResponse";

  // Utility methods
  static values() {
    return [SubTaskType.Ner, SubTaskType.Relations];
  }
}


export function getTaskTypeLabel(taskType, isMultilabel, classifySpeakers) {
  if (taskType === TaskType.classification) {
    return isMultilabel ? 'Classification (Multi-label)' : 'Classification (Single-label)';
    // TODO: speakers is deprecated.
    //return isMultilabel ? (classifySpeakers ? 'Classification (Multi & Speakers)' : 'Classification (Multi-label)') : 'Classification (Single-label)';
  }
  if (taskType === TaskType.summarization) {
    return 'Summarization';
  }
  if (taskType === TaskType.contentGeneration) {
    return 'Content Generation';
  }
  return taskType === TaskType.NER_WITH_RELATIONS ? 'Entity extraction & Relations' : 'Entity extraction';
}

export function getLabelColumnPluralLabel(taskType) {
  if (taskType === TaskType.classification) {
    return 'labels';
  }
  if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
    return 'entities';
  }
  if (taskType === TaskType.summarization) {
    return 'summaries';
  }
  if (taskType === TaskType.contentGeneration) {
    return 'responses';
  }
  return 'labels';
}
